import { styled } from "goober";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { zIndexes } from "../../lib/zIndexes";

const InfoTooltip = styled("div")`
  position: fixed;
  background: var(--text-primary);
  color: var(--text-invert);
  border-radius: 8px;
  padding: 8px 10px;
  box-sizing: border-box;
  width: max-content;
  max-width: calc(100vw / var(--ui-zoom, 1) - 16px);
  pointer-events: none;
  z-index: ${zIndexes.tooltip};
  font-size: 10px;
  line-height: 14px;
  overflow-wrap: anywhere;
  transform: translateY(-100%);
`;

// Positions against the chart svg's live rect and its own measured width
// (for viewport-edge flipping), which are post-render reads.
type Props = {
  svgRef: React.RefObject<SVGSVGElement | null>;
  anchorLeft: number;
  anchorTop: number;
  children: React.ReactNode;
};

export function AnchoredInfoTooltip({
  svgRef,
  anchorLeft,
  anchorTop,
  children,
}: Props) {
  const selfRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  useLayoutEffect(() => {
    const svg = svgRef.current;
    const tooltip = selfRef.current;
    if (!svg || !tooltip) return;
    const updatePosition = () => {
      const svgRect = svg.getBoundingClientRect();
      const zoom =
        Number.parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
      const viewportWidth = window.innerWidth / zoom;
      const viewportHeight = window.innerHeight / zoom;
      const tooltipWidth = tooltip.offsetWidth;
      const tooltipHeight = tooltip.offsetHeight;
      const baseLeft = svgRect.left / zoom + anchorLeft;
      const flipped = baseLeft + tooltipWidth > viewportWidth - 8;
      setPosition({
        left: Math.max(
          8,
          Math.min(
            flipped ? baseLeft - tooltipWidth - 16 : baseLeft,
            viewportWidth - tooltipWidth - 8
          )
        ),
        top: Math.min(
          viewportHeight - 8,
          Math.max(tooltipHeight + 8, svgRect.top / zoom + anchorTop)
        ),
      });
    };
    updatePosition();
    const observer = new ResizeObserver(updatePosition);
    observer.observe(svg);
    observer.observe(tooltip);
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    document.addEventListener("transitionend", updatePosition, true);
    document.addEventListener("animationend", updatePosition, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("transitionend", updatePosition, true);
      document.removeEventListener("animationend", updatePosition, true);
    };
  }, [svgRef, anchorLeft, anchorTop]);

  return createPortal(
    <InfoTooltip
      ref={selfRef}
      role="tooltip"
      style={
        position
          ? { left: position.left, top: position.top }
          : { visibility: "hidden" }
      }
    >
      {children}
    </InfoTooltip>,
    document.body
  );
}
